<template>
  <div>
    <!-- Content -->
    <div class="row">
      <div class="col-xl-12">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">{{ $t('superAdmin.users.list') }}</h5>
          </div>
          <div class="card-body">
            <DataGrid
              :data="formattedUsers"
              :columns="columns"
              v-model:currentPage="currentPage"
              v-model:pageSize="pageSize"
              v-model:searchText="searchTerm"
              v-model:sortField="sortField"
              v-model:sortDirection="sortDirection"
              :totalItems="totalItems"
              :loading="loading"
              :empty-state-message="$t('superAdmin.users.noData')"
              :search-placeholder="$t('superAdmin.users.searchPlaceholder')"
              :show-refresh="true"
              storage-key="userManagement_columnWidths"
              @search="searchUsers"
              @pageChange="loadUsers"
              @pageSizeChange="handlePageSizeChange"
              @sort="loadUsers"
              @refresh="loadUsers"
            >
              <!-- Tenant Filter Slot -->
              <template #filters>
                <select 
                  class="form-select form-select-sm" 
                  v-model="selectedTenantId"
                  style="min-width: 180px;"
                >
                  <option value="">{{ $t('superAdmin.users.allTenants') }}</option>
                  <option v-for="tenant in tenants" :key="tenant.id" :value="tenant.id">
                    {{ tenant.name }}
                  </option>
                </select>
              </template>

              <!-- Actions Dropdown Slot -->
              <template #actions="{ item }">
                <div class="btn-group position-relative">
                  <button 
                    class="btn btn-sm btn-outline-secondary dropdown-toggle" 
                    type="button" 
                    :id="`dropdownMenuButton${item.id}`"
                    @click="toggleDropdown(item.id, $event)"
                  >
                    <i class="ph-duotone ph-dots-three-vertical"></i>
                  </button>
                  <ul 
                    class="dropdown-menu dropdown-menu-end" 
                    :class="{ 'show': openDropdown === item.id }"
                    :aria-labelledby="`dropdownMenuButton${item.id}`"
                  >
                    <li>
                      <a 
                        class="dropdown-item" 
                        href="#" 
                        @click.prevent="executeAction(() => editUser(item._original))"
                        :title="$t('common.edit')"
                      >
                        <i class="ph-duotone ph-pencil me-2"></i>
                        {{ $t('common.edit') }}
                      </a>
                    </li>
                    <li>
                      <a 
                        class="dropdown-item" 
                        href="#" 
                        @click.prevent="executeAction(() => editPermissions(item._original))"
                        :title="$t('users.editPermissions')"
                      >
                        <i class="ph-duotone ph-shield me-2"></i>
                        {{ $t('users.editPermissions') }}
                      </a>
                    </li>
                    <li><hr class="dropdown-divider"></li>
                    <li>
                      <a 
                        class="dropdown-item" 
                        href="#"
                        @click.prevent="executeAction(() => sendPasswordReset(item._original))"
                        :class="{ 'disabled': sendingPasswordReset === item.id }"
                        :title="$t('users.passwordSetup.sendEmail')"
                      >
                        <span v-if="sendingPasswordReset === item.id" class="spinner-border spinner-border-sm me-2"></span>
                        <i v-else class="ph-duotone ph-key me-2"></i>
                        {{ $t('users.passwordSetup.sendEmail') }}
                      </a>
                    </li>
                    <li v-if="!item.emailConfirmedRaw">
                      <a 
                        class="dropdown-item" 
                        href="#"
                        @click.prevent="executeAction(() => resendEmailVerification(item._original))"
                        :class="{ 'disabled': sendingVerification === item.id }"
                        :title="$t('auth.login.resendVerification')"
                      >
                        <span v-if="sendingVerification === item.id" class="spinner-border spinner-border-sm me-2"></span>
                        <i v-else class="ph-duotone ph-envelope me-2"></i>
                        {{ $t('auth.login.resendVerification') }}
                      </a>
                    </li>
                    <li><hr class="dropdown-divider"></li>
                    <li>
                      <a 
                        class="dropdown-item text-danger" 
                        href="#"
                        @click.prevent="executeAction(() => confirmDeleteUser(item._original))"
                        :class="{ 'disabled': item.id === currentUserId }"
                        :title="$t('common.delete')"
                      >
                        <i class="ph-duotone ph-trash me-2"></i>
                        {{ $t('common.delete') }}
                      </a>
                    </li>
                  </ul>
                </div>
              </template>
            </DataGrid>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit User Modal -->
    <div
      class="modal fade"
      id="editUserModal"
      tabindex="-1"
      ref="editUserModal"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('superAdmin.users.editTitle') }}</h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
            ></button>
          </div>
          <div class="modal-body">
            <div v-if="editingUser.email !== editingUser.originalEmail || editingUser.tenantId !== editingUser.originalTenantId" class="alert alert-info">
              <i class="ph-duotone ph-info me-2"></i>
              <div v-if="editingUser.email !== editingUser.originalEmail">
                {{ $t('superAdmin.users.emailChangeWarning') }}
              </div>
              <div v-if="editingUser.tenantId !== editingUser.originalTenantId">
                {{ $t('superAdmin.users.tenantChangeWarning') }}
              </div>
            </div>
            <form @submit.prevent="saveUser">
              <div class="row">
                <div class="col-md-12">
                  <div class="mb-3">
                    <label class="form-label">{{ $t('superAdmin.users.form.email') }}</label>
                    <input
                      type="email"
                      class="form-control"
                      v-model="editingUser.email"
                      required
                    />
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-12">
                  <div class="mb-3">
                    <label class="form-label">{{ $t('superAdmin.users.form.tenant') }}</label>
                    <select class="form-select" v-model="editingUser.tenantId">
                      <option value="">{{ $t('superAdmin.users.superAdmin') }}</option>
                      <option v-for="tenant in tenants" :key="tenant.id" :value="tenant.id">
                        {{ tenant.name }}
                      </option>
                    </select>
                    <div class="form-text">{{ $t('superAdmin.users.form.tenantHelp') }}</div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">{{ $t('superAdmin.users.form.name') }}</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="editingUser.name"
                    />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">{{ $t('superAdmin.users.form.surname') }}</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="editingUser.surname"
                    />
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">{{ $t('superAdmin.users.form.phone') }}</label>
                    <input
                      type="tel"
                      class="form-control"
                      v-model="editingUser.phoneNumber"
                    />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <div class="form-check">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        v-model="editingUser.isActive"
                        id="isActiveCheck"
                      />
                      <label class="form-check-label" for="isActiveCheck">
                        {{ $t('superAdmin.users.form.isActive') }}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              {{ $t('common.cancel') }}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="saveUser"
              :disabled="saving"
            >
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              {{ $t('common.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      class="modal fade"
      id="deleteUserModal"
      tabindex="-1"
      ref="deleteUserModal"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title text-danger">{{ $t('superAdmin.users.deleteTitle') }}</h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
            ></button>
          </div>
          <div class="modal-body">
            <div class="alert alert-danger">
              <i class="ph-duotone ph-warning-circle me-2"></i>
              {{ $t('superAdmin.users.deleteWarning') }}
            </div>
            <p>{{ $t('superAdmin.users.deleteConfirm', { username: deletingUser?.email || deletingUser?.name || '-' }) }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              {{ $t('common.cancel') }}
            </button>
            <button
              type="button"
              class="btn btn-danger"
              @click="deleteUser"
              :disabled="deleting"
            >
              <span v-if="deleting" class="spinner-border spinner-border-sm me-2"></span>
              {{ $t('common.delete') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- User Permissions Modal -->
    <UserPermissionsModal :userId="selectedUserId" :isSuperAdmin="true" ref="permissionsModal" @error="handlePermissionError" />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { Modal } from 'bootstrap';
import dayjs from 'dayjs';
import superAdminService from '@/services/superAdminService';
import UserPermissionsModal from '@/components/UserPermissionsModal.vue';
import DataGrid from '@/components/DataGrid.vue';

const { t } = useI18n();
const store = useStore();

// Reactive data
const users = ref([]);
const tenants = ref([]);
const searchTerm = ref('');
const selectedTenantId = ref('');
const loading = ref(false);
const saving = ref(false);
const deleting = ref(false);
const sendingPasswordReset = ref(null);
const sendingVerification = ref(null);
const openDropdown = ref(null);
const editingUser = ref({});
const deletingUser = ref(null);
const selectedUserId = ref(null);

// Pagination
const currentPage = ref(1);
const pageSize = ref(10);
const totalItems = ref(0);

// Sorting
const sortField = ref('');
const sortDirection = ref('asc');

// Modal refs
const editUserModal = ref(null);
const deleteUserModal = ref(null);
const permissionsModal = ref(null);

// Bootstrap modals
let editModal;
let deleteModal;

// Computed
const currentUserId = computed(() => store.getters['auth/currentUser']?.id);

// DataGrid columns
const columns = computed(() => [
  { key: 'email', label: t('superAdmin.users.columns.email'), sortable: true, sortKey: 'email', type: 'truncate' },
  { key: 'name', label: t('superAdmin.users.columns.name'), sortable: true, sortKey: 'name' },
  { key: 'surname', label: t('superAdmin.users.columns.surname'), sortable: true, sortKey: 'surname' },
  { key: 'phoneNumber', label: t('superAdmin.users.columns.phone'), sortable: false },
  { key: 'tenantName', label: t('superAdmin.users.columns.tenant'), sortable: false },
  { key: 'emailConfirmed', label: t('superAdmin.users.columns.emailConfirmed'), type: 'badge', sortable: false },
  { key: 'hasUserManagementPermissions', label: t('superAdmin.users.columns.userManagementPermissions'), type: 'badge', sortable: false },
  { key: 'lastLoginTime', label: t('superAdmin.users.columns.lastLoginTime'), sortable: false }
]);

// Formatted data for DataGrid
const formattedUsers = computed(() => {
  return users.value.map(user => {
    const emailVerifiedIcon = user.emailConfirmed 
      ? '✓ Verified' 
      : '✗ Not Verified';
    
    return {
      id: user.id,
      email: user.email,
      emailSubtitle: user.emailConfirmed ? '' : '⚠️ Not Verified',
      name: user.name || '-',
      surname: user.surname || '-',
      phoneNumber: user.phoneNumber || '-',
      tenantName: user.tenantName || t('superAdmin.users.superAdmin'),
      emailConfirmed: emailVerifiedIcon,
      emailConfirmedColor: user.emailConfirmed ? '#28a745' : '#dc3545',
      emailConfirmedRaw: user.emailConfirmed,
      hasUserManagementPermissions: user.hasUserManagementPermissions ? t('common.values.yes') : t('common.values.no'),
      hasUserManagementPermissionsColor: user.hasUserManagementPermissions ? '#28a745' : '#6c757d',
      hasUserManagementPermissionsRaw: user.hasUserManagementPermissions,
      lastLoginTime: formatLastLoginTime(user.lastLoginTime),
      // Keep original data for actions
      _original: user
    };
  });
});

onMounted(async () => {
  editModal = new Modal(editUserModal.value);
  deleteModal = new Modal(deleteUserModal.value);
  
  // Close dropdown when clicking outside
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.btn-group')) {
      openDropdown.value = null;
    }
  });
  
  await loadTenants();
  loadUsers();
});

// Watch for tenant changes
watch(selectedTenantId, () => {
  currentPage.value = 1;
  loadUsers();
});

onBeforeUnmount(() => {
  document.removeEventListener('click', (e) => {
    if (!e.target.closest('.btn-group')) {
      openDropdown.value = null;
    }
  });
});

// Methods
const loadTenants = async () => {
  try {
    // Request all tenants with large page size for dropdown
    const response = await superAdminService.getTenants({ 
      page: 1, 
      pageSize: 1000 
    });
    
    // Handle paginated response
    if (Array.isArray(response)) {
      tenants.value = response;
    } else if (response?.items) {
      tenants.value = response.items;
    } else {
      tenants.value = [];
    }
  } catch (error) {
    console.error('Error loading tenants:', error);
    tenants.value = [];
  }
};

const loadUsers = async () => {
  loading.value = true;
  try {
    const response = await superAdminService.getUsers(
      searchTerm.value, 
      selectedTenantId.value, 
      currentPage.value, 
      pageSize.value,
      {
        sortField: sortField.value,
        sortDirection: sortDirection.value
      }
    );
    
    // Handle response - could be array or object with items and totalCount
    if (Array.isArray(response)) {
      users.value = response;
      totalItems.value = response.length;
    } else if (response.items) {
      users.value = response.items;
      totalItems.value = response.totalCount || response.rowCount || response.items.length;
    } else {
      users.value = [];
      totalItems.value = 0;
    }
  } catch (error) {
    console.error('Error loading users:', error);
    store.dispatch('notify', {
      message: t('superAdmin.users.errors.loadFailed'),
      type: 'error'
    });
    users.value = [];
    totalItems.value = 0;
  } finally {
    loading.value = false;
  }
};

const searchUsers = () => {
  currentPage.value = 1;
  loadUsers();
};

const handlePageSizeChange = () => {
  currentPage.value = 1;
  loadUsers();
};

const editUser = (user) => {
  editingUser.value = { 
    ...user, 
    originalEmail: user.email,
    originalTenantId: user.tenantId
  };
  editModal.show();
};

const editPermissions = async (user) => {
  selectedUserId.value = user.id;
  
  // Wait for next tick to ensure userId is set
  await new Promise(resolve => setTimeout(resolve, 0));
  
  const modalElement = document.getElementById('userPermissionsModal');
  const modal = new Modal(modalElement);
  
  // Load permissions after modal is shown
  modalElement.addEventListener('shown.bs.modal', async () => {
    if (permissionsModal.value) {
      await permissionsModal.value.loadUserPermissions();
    }
  }, { once: true });
  
  modal.show();
};

const saveUser = async () => {
  saving.value = true;
  try {
    // Email değerini username olarak da gönder
    const userData = {
      ...editingUser.value,
      username: editingUser.value.email
    };
    await superAdminService.updateUser(userData);
    editModal.hide();
    
    const hasEmailChange = editingUser.value.email !== editingUser.value.originalEmail;
    const hasTenantChange = editingUser.value.tenantId !== editingUser.value.originalTenantId;
    
    if (hasEmailChange && hasTenantChange) {
      store.dispatch('notify', {
        message: t('superAdmin.users.success.emailAndTenantChanged'),
        type: 'info'
      });
    } else if (hasEmailChange) {
      store.dispatch('notify', {
        message: t('superAdmin.users.success.emailChanged'),
        type: 'info'
      });
    } else if (hasTenantChange) {
      store.dispatch('notify', {
        message: t('superAdmin.users.success.tenantChanged'),
        type: 'success'
      });
    } else {
      store.dispatch('notify', {
        message: t('superAdmin.users.success.updated'),
        type: 'success'
      });
    }
    
    loadUsers();
  } catch (error) {
    console.error('Error saving user:', error);
    store.dispatch('notify', {
      message: t('superAdmin.users.errors.updateFailed'),
      type: 'error'
    });
  } finally {
    saving.value = false;
  }
};

const sendPasswordReset = async (user) => {
  if (!confirm(t('superAdmin.users.passwordResetConfirm', { username: user.email || user.name || '-' }))) {
    return;
  }

  sendingPasswordReset.value = user.id;
  try {
    await superAdminService.sendPasswordReset(user.id);
    store.dispatch('notify', {
      message: t('superAdmin.users.success.passwordResetSent'),
      type: 'success'
    });
  } catch (error) {
    console.error('Error sending password reset:', error);
    store.dispatch('notify', {
      message: t('superAdmin.users.errors.passwordResetFailed'),
      type: 'error'
    });
  } finally {
    sendingPasswordReset.value = null;
  }
};

const resendEmailVerification = async (user) => {
  sendingVerification.value = user.id;
  try {
    await superAdminService.resendEmailVerification(user.email);
    store.dispatch('notify', {
      message: t('superAdmin.users.success.verificationSent'),
      type: 'success'
    });
  } catch (error) {
    console.error('Error resending verification:', error);
    store.dispatch('notify', {
      message: t('superAdmin.users.errors.verificationFailed'),
      type: 'error'
    });
  } finally {
    sendingVerification.value = null;
  }
};

const confirmDeleteUser = (user) => {
  if (user.id === currentUserId.value) {
    store.dispatch('notify', {
      message: t('superAdmin.users.errors.cannotDeleteSelf'),
      type: 'error'
    });
    return;
  }
  
  deletingUser.value = user;
  deleteModal.show();
};

const deleteUser = async () => {
  deleting.value = true;
  try {
    await superAdminService.deleteUser(deletingUser.value.id);
    deleteModal.hide();
    store.dispatch('notify', {
      message: t('superAdmin.users.success.deleted'),
      type: 'success'
    });
    loadUsers();
  } catch (error) {
    console.error('Error deleting user:', error);
    
    // Get specific error message from API response
    let errorMessage = t('superAdmin.users.errors.deleteFailed');
    
    if (error && error.response && error.response.data && error.response.data.error) {
      const apiError = error.response.data.error;
      if (apiError.message) {
        errorMessage = apiError.message;
      }
    } else if (error && error.message) {
      errorMessage = error.message;
    }
    
    store.dispatch('notify', {
      message: errorMessage,
      type: 'error'
    });
  } finally {
    deleting.value = false;
  }
};

const handlePermissionError = (error) => {
  let errorMessage = t('superAdmin.users.errors.permissionUpdateFailed');
  
  // Check if error has the specific API format
  if (error && error.response && error.response.data && error.response.data.error) {
    const apiError = error.response.data.error;
    if (apiError.message) {
      errorMessage = apiError.message;
    }
  } else if (error && error.message) {
    errorMessage = error.message;
  }
  
  store.dispatch('notify', {
    message: errorMessage,
    type: 'error'
  });
};

const formatLastLoginTime = (date) => {
  if (!date) return '-';
  
  // Check if date is before 1902 (invalid/default date)
  const dateObj = dayjs(date);
  if (dateObj.year() < 1902) return '-';
  
  const fmt = store.state.settings?.general?.dateFormat || 'DD/MM/YYYY';
  return dateObj.format(`${fmt} HH:mm`);
};

const toggleDropdown = (userId, event) => {
  if (openDropdown.value === userId) {
    openDropdown.value = null;
  } else {
    openDropdown.value = userId;
    
    // Position the dropdown menu using fixed positioning
    nextTick(() => {
      const button = event?.currentTarget || document.getElementById(`dropdownMenuButton${userId}`);
      const dropdown = button?.nextElementSibling;
      
      if (button && dropdown) {
        const buttonRect = button.getBoundingClientRect();
        dropdown.style.top = `${buttonRect.bottom + 4}px`;
        dropdown.style.right = `${window.innerWidth - buttonRect.right}px`;
        dropdown.style.left = 'auto';
      }
    });
  }
};

const executeAction = (action) => {
  openDropdown.value = null; // Close dropdown
  action(); // Execute the action
};
</script>

<style scoped>
/* Dropdown actions styling */
.btn-group {
  position: relative;
}

.dropdown-menu {
  min-width: 200px;
  max-width: 280px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid #e9ecef;
  background-color: white;
  position: fixed !important;
  z-index: 9999 !important;
  margin-top: 4px;
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  font-size: 0.875rem;
  white-space: nowrap;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.dropdown-item:disabled {
  opacity: 0.6;
  pointer-events: none;
}

.dropdown-item.text-danger:hover {
  background-color: #f8d7da;
  color: #721c24;
}
</style> 